import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ConceptTree, FormColumn, FormField } from '../schema/types';
import './form-field';

@customElement('jupiter-concept-tree')
export class JupiterConceptTree extends LitElement {
  @property({ type: Object }) concept!: ConceptTree;
  @property({ type: Array }) columns: FormColumn[] = [];
  @property({ type: Object }) formData: Record<string, any> = {};
  @property({ type: Boolean }) disabled = false;
  @property({ type: String }) locale = 'en-US';
  @property({ type: Set }) expandedConcepts: Set<string> = new Set();

  @state() private _expanded = true;

  static styles = css`
    :host {
      /* Component renders table cells directly */
      display: contents;
    }

    .concept-name-cell {
      vertical-align: top;
      padding: 2px 4px;
      border: 1px solid var(--jupiter-border-color, #ddd);
      border-bottom: none;
      background: var(--jupiter-concept-background, #f8f9fa);
      position: sticky;
      left: 0;
      z-index: 1;
      width: 300px;
      min-width: 300px;
      max-width: 300px;
    }

    .concept-name-cell.abstract {
      background: var(--jupiter-abstract-background, #f0f2f5);
      font-weight: 600;
    }

    .concept-name-cell.leaf {
      background: var(--jupiter-leaf-background, #fff);
      font-weight: 400;
    }

    .concept-content {
      display: flex;
      align-items: center;
      min-height: 28px;
      font-size: 13px;
      line-height: 1.3;
      width: 100%;
      overflow: hidden;
    }

    .concept-indent {
      width: calc(var(--level, 0) * 16px);
      flex-shrink: 0;
    }

    .concept-toggle {
      width: 14px;
      height: 14px;
      margin-right: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 10px;
      transition: transform 0.2s ease;
      cursor: pointer;
    }

    .concept-toggle.expanded {
      transform: rotate(90deg);
    }

    .concept-toggle.no-children {
      visibility: hidden;
    }

    .concept-label {
      flex: 1;
      color: var(--jupiter-text-primary, #333);
      cursor: pointer;
      word-wrap: break-word;
      overflow-wrap: break-word;
      hyphens: auto;
      line-height: 1.3;
      min-width: 0; /* Allows flex item to shrink below content size */
    }

    .field-cell {
      vertical-align: middle;
      padding: 2px 6px;
      border: 1px solid var(--jupiter-border-color, #ddd);
      border-bottom: none;
      background: var(--jupiter-cell-background, #fff);
      min-height: 28px;
      text-align: center;
      min-width: 180px;
      width: 180px;
    }

    .field-cell.empty {
      background: var(--jupiter-empty-cell-background, #f8f9fa);
    }

    .field-cell.abstract-row {
      background: var(--jupiter-abstract-cell-background, #f0f2f5);
    }

    /* Children are rendered as separate table rows, not nested */
    .children-wrapper {
      display: none;
    }


  `;

  connectedCallback() {
    super.connectedCallback();
    this._expanded = this.expandedConcepts.has(this.concept.id);
  }

  willUpdate(changedProperties: Map<string, unknown>) {
    super.willUpdate(changedProperties);
    if (changedProperties.has('expandedConcepts')) {
      this._expanded = this.expandedConcepts.has(this.concept.id);
    }
  }

  private _toggleExpanded() {
    this._expanded = !this._expanded;
    
    this.dispatchEvent(new CustomEvent('concept-expand', {
      detail: {
        conceptId: this.concept.id,
        expanded: this._expanded
      },
      bubbles: true
    }));
  }

  private _getFieldForColumn(columnId: string): FormField | undefined {
    return this.concept.fields?.find(field => field.columnId === columnId);
  }

  private _getFieldValue(field: FormField): any {
    return this.formData[this.concept.id]?.[field.columnId];
  }

  private _handleFieldChange(event: CustomEvent) {
    event.stopPropagation();
    // Re-dispatch with updated detail
    this.dispatchEvent(new CustomEvent('field-change', {
      detail: event.detail,
      bubbles: true
    }));
  }

  render() {
    const hasChildren = this.concept.children && this.concept.children.length > 0;
    const level = this.concept.level || 0;
    const isAbstract = this.concept.abstract || false;

    return html`
      <!-- Concept Name Cell (Left Column) -->
      <td class="concept-name-cell ${isAbstract ? 'abstract' : (hasChildren ? '' : 'leaf')}">
        <div class="concept-content" style="--level: ${level}">
          <div class="concept-indent"></div>
          <div class="concept-toggle ${hasChildren ? (this._expanded ? 'expanded' : '') : 'no-children'}"
               @click="${this._toggleExpanded}">
            ${hasChildren ? '▶' : ''}
          </div>
          <div class="concept-label" 
               @click="${this._toggleExpanded}"
               title="${this.concept.id}${this.concept.description ? ' - ' + this.concept.description : ''}">
            ${this.concept.label}
          </div>
        </div>
      </td>

      <!-- Input Field Cells (Period Columns) - Only for non-abstract concepts -->
      ${this.columns.map(column => {
        const field = this._getFieldForColumn(column.id);
        const shouldShowField = !isAbstract && field;
        
        return html`
          <td class="field-cell ${!shouldShowField ? 'empty' : ''} ${isAbstract ? 'abstract-row' : ''}">
            ${shouldShowField ? html`
              <jupiter-form-field
                .field="${field}"
                .conceptId="${this.concept.id}"
                .columnId="${column.id}"
                .value="${this._getFieldValue(field)}"
                .disabled="${this.disabled}"
                .locale="${this.locale}"
                .hideLabel="${true}"
                @field-change="${this._handleFieldChange}"
              ></jupiter-form-field>
            ` : ''}
          </td>
        `;
      })}
    `;
  }
}
